import React from 'react';

import { DataGroupItemResponse, UserDataGroupResponse } from '@ariksa/abac/api';
import { JitDataGroupStatusesResponse } from '@ariksa/jit/api';
import { Box, HStack, Stack, Wrap } from '@chakra-ui/react';
import { colorOpacity, customTheme } from 'theme';

import { CustomTooltip } from 'components/DataDisplay';
import { IconTypes, Tag } from 'components/index';

// Chips under a field: the chosen permissions, or the role's access levels.
// Both cases add the same 8px below the field, so switching access type keeps
// the form's height. The first chip row (32px) sits in the gap, cancelled by
// the negative margins; only further rows add height.
export const renderPermissions = ({
  labels,
  color = customTheme.colors.blue['300'],
}: Record<string, any>) =>
  labels.length ? (
    <Box mt={-2} mb={-6}>
      <Wrap spacing={2} pt={2}>
        {(labels as string[]).map(label => (
          <Tag
            key={label}
            label={label}
            styles={{
              tag: {
                h: 6,
                minH: 6,
                bg: colorOpacity(color, 0.1),
                border: 'none',
              },
              label: { textTransform: 'uppercase', color },
            }}
          />
        ))}
      </Wrap>
    </Box>
  ) : (
    <Box mt={-2} h={2} />
  );

export const getDataSourceDetails = (item: DataGroupItemResponse) => {
  if (item.table_name) {
    return {
      name: item.table_name,
      label: 'Snowflake Table',
      resourceType: IconTypes.SnowflakeTables,
    };
  }

  if (item.schema_name) {
    return {
      name: item.schema_name,
      label: 'Snowflake Schema',
      resourceType: IconTypes.SnowflakeTables,
    };
  }

  return {
    name: item.database_name,
    label: 'Snowflake Database',
    resourceType: IconTypes.SnowflakeDatabases,
  };
};

export const getRoles = (dataGroup: UserDataGroupResponse) => {
  const roles = [
    ...(dataGroup.permanent_access?.roles ?? []),
    ...(dataGroup.eligible_access?.roles ?? []),
  ];

  return roles.filter(
    (role, index) =>
      !!role?.name &&
      roles.findIndex(item => item.name === role.name) === index,
  );
};

export const renderPrivileges = (dataGroup: UserDataGroupResponse) => {
  const roles = getRoles(dataGroup);
  const privileges = Array.from(
    new Set(roles.flatMap(role => role.crud_levels ?? [])),
  );

  if (!privileges.length) return '-';

  return (
    <CustomTooltip
      label={
        <Stack spacing={1}>
          {roles.map(role => (
            <Box key={role.name}>{role.name}</Box>
          ))}
        </Stack>
      }
    >
      <HStack spacing={2} flexWrap="wrap">
        {privileges.map(privilege => (
          <Tag
            key={privilege}
            label={privilege.toUpperCase()}
            styles={{
              tag: {
                bg: customTheme.colors.gray['50'],
                border: 'none',
                borderRadius: 3,
              },
              label: { color: customTheme.colors.gray['300'] },
            }}
          />
        ))}
      </HStack>
    </CustomTooltip>
  );
};

export const getDataGroupStatus = (
  statuses: JitDataGroupStatusesResponse,
  row: UserDataGroupResponse,
  onClick,
) => {
  const status = statuses.data_groups?.find(
    dataGroup => dataGroup.data_group_id === row.uuid,
  );

  const renderTag = (label, color) => (
    <Tag
      label={label}
      styles={{
        tag: {
          bg: colorOpacity(color, 0.2),
          borderColor: customTheme.colors.gray['100'],
        },
        label: { color: customTheme.colors.gray['300'] },
      }}
      onClick={onClick}
    />
  );

  if (status?.has_active_request)
    return renderTag('Active', customTheme.colors.green['300']);
  if (status?.has_pending_request)
    return renderTag('Under Review', customTheme.colors.yellow['300']);
  if (status?.has_approved_request)
    return renderTag('Approved', customTheme.colors.green['300']);

  return '';
};

export const viewDataCatalogByOptions = [
  { label: 'Data Groups', value: 'data-group' },
  { label: 'Data Source', value: 'data-source', isDisabled: true },
];
